/**
 * Splits a curated journey slug — `<from>-to-<to>` — back into its two station
 * slugs.
 *
 * These URLs are minted by `SiteUrls.routePath` (`scripts/siteUrls.mjs`),
 * published in the `sitemap-routes-*.xml` parts, and linked from every prerendered station
 * page, so the shape is a fixed contract: change it here and 464 indexed URLs
 * stop resolving.
 *
 * WHY THIS IS NOT `pair.split('-to-')`: a station slug may itself contain
 * `-to-`, and nothing forbids one appearing on either side. The separator is
 * therefore ambiguous by construction, and the only authority on where the seam
 * lies is the slug set itself. Every candidate split is tried and the one whose
 * BOTH halves resolve wins.
 *
 * Ambiguity that survives that check (both halves resolving at two different
 * seams) would mean two real stations whose slugs overlap across the joint;
 * the leftmost split is returned, matching how the URL was built.
 */
export interface RoutePair {
  readonly fromSlug: string
  readonly toSlug: string
}

const SEPARATOR = '-to-'

export function parseRoutePairSlug(
  pair: string | undefined,
  knownSlugs: ReadonlySet<string> | ReadonlyMap<string, unknown>,
): RoutePair | null {
  if (pair === undefined || pair === '') return null

  const has = (slug: string): boolean =>
    knownSlugs instanceof Set
      ? knownSlugs.has(slug)
      : (knownSlugs as ReadonlyMap<string, unknown>).has(slug)

  let index = pair.indexOf(SEPARATOR)
  while (index !== -1) {
    const fromSlug = pair.slice(0, index)
    const toSlug = pair.slice(index + SEPARATOR.length)
    if (fromSlug !== '' && toSlug !== '' && has(fromSlug) && has(toSlug)) {
      return { fromSlug, toSlug }
    }
    index = pair.indexOf(SEPARATOR, index + 1)
  }

  return null
}
